import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { APPLICATION_STATUSES } from "@/lib/constants";
import { TrendingUp, Globe, DollarSign } from "lucide-react";

export default function AdminAnalytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get("/admin/analytics");
        setData(res.data);
      } catch {
        // ignore
      }
    };
    load();
  }, []);

  if (!data) return null;

  const { totals, conversion, applications_by_status, offers_by_country, candidates_by_country } = data;

  const statusLabel = (v) => APPLICATION_STATUSES.find((s) => s.value === v)?.label || v;

  return (
    <div data-testid="admin-analytics" className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
      <h2 className="font-semibold text-brand-navy flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-brand-turquoise" />
        Statistiques (30 derniers jours)
      </h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-brand-sky/50 rounded-lg p-4">
          <p className="text-xs text-slate-500 mb-1">Conversion CV → candidature</p>
          <p data-testid="analytics-cv-conversion" className="text-xl font-bold text-brand-navy">{conversion.cv_to_application}%</p>
        </div>
        <div className="bg-brand-sky/50 rounded-lg p-4">
          <p className="text-xs text-slate-500 mb-1">Candidature → embauche</p>
          <p data-testid="analytics-hire-conversion" className="text-xl font-bold text-brand-navy">{conversion.application_to_hire}%</p>
        </div>
        <div className="bg-brand-sky/50 rounded-lg p-4">
          <p className="text-xs text-slate-500 mb-1">Embauches</p>
          <p data-testid="analytics-hired" className="text-xl font-bold text-brand-turquoise">{totals.hired}</p>
        </div>
        <div className="bg-brand-sky/50 rounded-lg p-4">
          <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><DollarSign className="w-3 h-3" /> Revenus</p>
          <p data-testid="analytics-revenue" className="text-xl font-bold text-brand-navy">
            {(totals.revenue_cents / 100).toLocaleString("fr-CA", { style: "currency", currency: "CAD" })}
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div>
          <p className="text-sm font-medium text-brand-navy mb-3">Candidatures par statut</p>
          <div className="space-y-2">
            {APPLICATION_STATUSES.map((s) => {
              const count = applications_by_status[s.value] || 0;
              const max = Math.max(...Object.values(applications_by_status), 1);
              return (
                <div key={s.value} className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-24 shrink-0">{s.label}</span>
                  <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-brand-blue rounded-full" style={{ width: `${(count / max) * 100}%` }} />
                  </div>
                  <span data-testid={`analytics-status-${s.value}`} className="text-xs font-medium text-brand-navy w-6 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <p className="text-sm font-medium text-brand-navy mb-3 flex items-center gap-1.5">
            <Globe className="w-4 h-4 text-brand-blue" /> Offres par pays
          </p>
          <div className="space-y-1.5">
            {Object.entries(offers_by_country).map(([country, count]) => (
              <div key={country} className="flex justify-between text-sm">
                <span className="text-slate-600">{country || "—"}</span>
                <span className="font-medium text-brand-navy">{count}</span>
              </div>
            ))}
            {Object.keys(offers_by_country).length === 0 && <p className="text-xs text-slate-400">Aucune donnée.</p>}
          </div>
        </div>
        <div>
          <p className="text-sm font-medium text-brand-navy mb-3 flex items-center gap-1.5">
            <Globe className="w-4 h-4 text-brand-turquoise" /> Candidats par pays
          </p>
          <div className="space-y-1.5">
            {Object.entries(candidates_by_country).map(([country, count]) => (
              <div key={country} className="flex justify-between text-sm">
                <span className="text-slate-600">{country || "—"}</span>
                <span className="font-medium text-brand-navy">{count}</span>
              </div>
            ))}
            {Object.keys(candidates_by_country).length === 0 && <p className="text-xs text-slate-400">Aucune donnée.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
